import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsExclamationTriangleFill } from "react-icons/bs";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiPaths } from "../../../api/paths";
import GridDetailItem from "../../../components/grid-detail-item/GridDetailItem";
import DropdownIcon from "../../../components/icons/DropdownIcon";
import useData from "../../../hooks/use-data";
import { GridItemType } from "../../../types/common";
import { CoverType } from "../../../types/cover";
import { getStringDate } from "../../../utils/utc-date-conversion";
import useDisclosure from "../../../hooks/use-disclosure";
import TraditionalCovers from "../traditional-covers-page/TraditionalCovers";

export default function TraditionalCoverDetails() {
  const { coverId } = useParams();
  const {
    onHide: onHideTraditionalCovers,
    onShow: onShowTraditionalCovers,
    show: showTraditionalCovers,
  } = useDisclosure();
  const { t } = useTranslation(["common", "myCovers"]);
  const [coverAction, setCoverAction] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [redirectURL, setRedirectURL] = useState("");
  const { data, loading } = useData<CoverType>(
    `${ApiPaths.covers}/${coverId}`,
    {
      onError: (err) => {
        toast(err.response?.data?.message || "An error occurred", {
          type: "error",
        });
      },
    }
  );

  const request_details_map: GridItemType[] = [
    {
      accessor: "_id",
      label: t("details.label.cover.id", { ns: "myCovers" }),
      value: (val) => {
        return `#${val?._id}`;
      },
    },
    {
      accessor: "created_at",
      label: t("details.label.request.request_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val?.cover_end_date),
    },
    {
      accessor: "approved_at",
      label: t("details.label.request.approved_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val?.cover_end_date),
    },
    {
      accessor: "active_at",
      label: t("details.label.request.active_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val?.cover_start_date),
    },
  ];

  const client_details_map: GridItemType[] = [
    {
      accessor: "firstName",
      label: t("details.label.client.first_name", { ns: "myCovers" }),
      value: (val) => val?.client_name?.name,
    },
    {
      accessor: "lastName",
      label: t("details.label.client.last_name", { ns: "myCovers" }),
      value: (val) => val?.client_name?.name,
    },
    {
      accessor: "email",
      label: t("details.label.client.email", { ns: "myCovers" }),
      value: (val) => val?.email,
    },
  ];

  const cover_details_map: GridItemType[] = [
    {
      accessor: "product",
      label: t("details.label.cover.id", { ns: "myCovers" }),
      value: (val) => val?._id,
    },
    {
      accessor: "product_name",
      label: t("details.label.cover.product_name", { ns: "myCovers" }),
      value: (val) => val?.product?.product_name,
    },
    {
      accessor: "product_type",
      label: t("details.label.cover.product_type", { ns: "myCovers" }),
      value: (val) =>
        val?.product?.product_type?.map((item: any) => item?.label).join(", "),
    },
    {
      accessor: "cover_amount",
      label: t("details.label.cover.amount", { ns: "myCovers" }),
      value: (val) => (
        <p>
          {new Intl.NumberFormat("en-us", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(val?.cover_amount)}
        </p>
      ),
    },
    {
      accessor: "cover_premium_amount",
      label: t("details.label.cover.premium", { ns: "myCovers" }),
      value: (val) => (
        <p>
          {new Intl.NumberFormat("en-us", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(val?.cover_premium_amount)}
        </p>
      ),
    },
    {
      accessor: "cover_start_date",
      label: t("details.label.cover.start_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val?.cover_start_date),
    },
    {
      accessor: "cover_end_date",
      label: t("details.label.cover.end_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val?.cover_end_date),
    },
    {
      accessor: "period",
      label: t("details.label.cover.period", { ns: "myCovers" }),
      value: (val) => `${val?.period} Days`,
    },
  ];

  const handleSubmitSelectionTraditionalCovers = () => {
    if (selectedOption === "cancel") {
      setRedirectURL(
        "https://app.clustdoc.com/cp/10546640-gyC789fsDZl5c7tbTVIA9xAPpNJMYW"
      );
      setCoverAction("Cancel Cover");
      onShowTraditionalCovers();
      return;
    }
    if (selectedOption === "raise") {
      setRedirectURL(
        "https://app.clustdoc.com/cp/10546554-wUJxkbjTL9AsHdbbopTHmcVVUaeIcF"
      );
      setCoverAction("Make Claims");
      onShowTraditionalCovers();
      return;
    }
    if (selectedOption === "extend") {
      setRedirectURL(
        "https://app.clustdoc.com/cp/10546638-pP04nOh7iLdcTuskNaQDPTTjgu1Qit"
      );
      setCoverAction("Extend Cover");
      onShowTraditionalCovers();
      return;
    }
    toast("Please select an action first", {
      type: "error",
      toastId: "cover-option",
    });
  };

  return (
    <div className="p-3 pb-5 mb-5">
      {/* Traditional Covers Modal */}
      <TraditionalCovers
        onHide={onHideTraditionalCovers}
        show={showTraditionalCovers}
        coverAction={coverAction}
        redirectURL={redirectURL}
      />

      {/* Title and Dropdown Section */}
      <div className="d-md-flex align-items-center justify-content-between mb-3">
        <h2 className="primary-title font-14 mb-3 mb-md-0 ">
          {t("details.heading.cover", { ns: "myCovers" })}
        </h2>
        <div className="d-flex justify-content-between justify-content-md-end align-items-center">
          <div className="position-relative me-md-4">
            <div className="drop-down-icon">
              <DropdownIcon />
            </div>
            <select
              name=""
              id=""
              className="covers-drop-down border-0"
              onChange={(e) => setSelectedOption(e.target.value)}
              disabled={loading} // Disable the dropdown while loading
            >
              <option value="">
                {t("details.placeholder.action", { ns: "myCovers" })}
              </option>
              <option value="cancel">
                {t("details.options.cancel", { ns: "myCovers" })}
              </option>
              <option value="extend">
                {t("details.options.extend", { ns: "myCovers" })}
              </option>
              <option value="raise">
                {t("details.options.raise", { ns: "myCovers" })}
              </option>
            </select>
          </div>
          <button
            className="primary-button text-decoration-none buy-covers-button"
            style={{ whiteSpace: "nowrap" }}
            onClick={handleSubmitSelectionTraditionalCovers}
            disabled={loading} // Disable the button while loading
          >
            {t("button.submit", { ns: "common" })}
          </button>
        </div>
      </div>

      {/* Loader when data is loading */}
      {loading && (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Main Content - Display Data if Available */}
      {data && !loading && (
        <div className="box-shadow px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-3">
          <div className="claim-details-status pb-3 mb-3 ">
            <h2 className="primary-title font-14 ">
              {t("details.heading.client", { ns: "myCovers" })}
            </h2>
          </div>
          <div className="client-details-cont d-grid">
            {client_details_map.map((item) => {
              return (
                <GridDetailItem
                  key={item.accessor}
                  label={item.label}
                  data={item.value(data)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Error Message or Cover Details */}
      {!data && !loading && (
        <div className="box-shadow px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-5 text-center">
          <BsExclamationTriangleFill className="mb-2 details-error-icon" />
          <h2 className="font-16">
            {t("details.error.fetch_details", { ns: "myCovers" })}
          </h2>
        </div>
      )}

      {/* Display request and cover details if data is available */}
      {data && !loading && (
        <>
          <div className="px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-3 box-shadow">
            <div className="claim-details-status d-flex justify-content-between align-items-center pb-3 mb-3 ">
              <h2 className="primary-title font-14">
                {t("details.heading.request", { ns: "myCovers" })}
              </h2>
              <div>
                <p className="d-none d-md-inline secondary-color font-14 me-1">
                  {t("details.label.request.status", { ns: "myCovers" })}:
                </p>
                <span
                  className={`status-button ${data?.cover_status.toLowerCase()}-status py-2 px-3 fw-bold`}
                >
                  {data?.cover_status}
                </span>
              </div>
            </div>
            <div className="claim-details-header d-grid ">
              {request_details_map.map((item) => {
                return (
                  <GridDetailItem
                    key={item.accessor}
                    label={item.label}
                    data={item.value(data)}
                  />
                );
              })}
            </div>
          </div>
          <div className="box-shadow px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-3">
            <div className="claim-details-status d-flex justify-content-between  pb-3 mb-3 ">
              <h2 className="primary-title font-14 ">
                {t("details.heading.cover", { ns: "myCovers" })}
              </h2>
              {data && (
                <div>
                  <p className="d-none d-md-inline secondary-color font-14 me-1">
                    {t("details.label.cover.status", { ns: "myCovers" })}:
                  </p>
                  <span
                    className={`status-button ${data?.cover_status.toLowerCase()}-status py-2 px-3 fw-bold`}
                  >
                    {data?.cover_status}
                  </span>
                </div>
              )}
            </div>
            <div className="client-details client-details-cont d-grid">
              {cover_details_map.map((item) => {
                return (
                  <GridDetailItem
                    key={item.accessor}
                    label={item.label}
                    data={item.value(data)}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
